import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Spinner } from "@/components/primitives";
import { Icon } from "@/components/Icon";
import { completeMagicLink, parseMagicLinkFragment } from "@/lib/auth";

type State = "working" | "failed";

/**
 * Landing page for an emailed sign-in link.
 *
 * The secret arrives in the URL fragment (never the query string, so it stays
 * out of CloudFront logs). It's redeemed once on mount and the fragment is
 * stripped immediately so a copied URL or a back-navigation can't replay it.
 */
export function MagicLink() {
  const navigate = useNavigate();
  const [state, setState] = useState<State>("working");
  const [error, setError] = useState<string | null>(null);
  // React 18 StrictMode double-invokes effects in dev; the challenge answer is
  // single-use, so a second attempt would fail a sign-in that actually worked.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const parsed = parseMagicLinkFragment(window.location.hash);
    // Drop the secret from the address bar before doing anything else.
    window.history.replaceState(null, "", window.location.pathname);

    if (!parsed) {
      setError("This sign-in link is missing its token. Request a new one.");
      setState("failed");
      return;
    }

    completeMagicLink(parsed.token)
      .then(() => navigate("/", { replace: true }))
      .catch((err: Error) => {
        setError(err.message);
        setState("failed");
      });
  }, [navigate]);

  return (
    <div style={{ display: "grid", placeItems: "center", padding: 48 }}>
      <Card style={{ width: 380, padding: 24 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 18,
          }}
        >
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "var(--primary)",
              color: "var(--primary-foreground)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon name="pulse" size={18} />
          </span>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Status Watch</div>
        </div>

        {state === "working" ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 13,
              color: "var(--muted-foreground)",
            }}
          >
            <Spinner />
            Signing you in…
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div
              style={{
                fontSize: 12,
                color: "var(--destructive)",
                padding: 10,
                borderRadius: 8,
                background:
                  "color-mix(in oklch, var(--destructive) 10%, transparent)",
                lineHeight: 1.5,
              }}
            >
              {error}
            </div>
            <Button
              variant="primary"
              onClick={() => navigate("/signin", { replace: true })}
            >
              Back to sign in
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
